import { Box, Link } from '@mui/material';
import { TournamentType } from '@okletsplay/core';
import { Route } from 'app/enums';
import { TournamentEntity, UserEntity } from 'app/interfaces/entity';
import { TournamentAdmins } from 'modules/tournaments/components/BracketTournament/TournamentAdmins';
import { TournamentRules } from 'modules/tournaments/components/BracketTournament/TournamentRules';
import { TournamentSupport } from 'modules/tournaments/components/BracketTournament/TournamentSupport';
import { TournamentDescription } from 'modules/tournaments/components/TournamentDescription';
import { TournamentSettings } from 'modules/tournaments/components/TournamentDetails';
import { TournamentPrizes } from 'modules/tournaments/components/TournamentModal/TournamentPrizes';
import { useTournamentSettings, useTournamentCurrentPrizes } from 'modules/tournaments/hooks';
import { TournamentSettingsOption } from 'modules/tournaments/interfaces';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TeamSizeFormat } from 'shared/components/Format/TeamSizeFormat';
import { TournamentTypeFormat } from 'shared/components/Format/TournamentTypeFormat';
import { GameCurrency, GameCurrencyWithFee } from 'modules/game-payments/components';
import { ValueOrDefaultFormat } from 'shared/components/Format/ValueOrDefaultFormat';
import { Info } from 'shared/components/Info';
import { useTournamentMatchSetupSettings } from 'modules/tournaments/hooks/use-tournament-match-setup-settings';

export const TournamentOverview: FC = () => {
  const navigate = useNavigate();
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const isSinglePlayer = useSelector(currentTournamentSelector.isSinglePlayer);
  const { teamsSize } = useTournamentSettings(tournament);

  const { currentTotalPrize, currentDistributionBreakdown } =
    useTournamentCurrentPrizes(tournament);

  const openBracketHandler = useCallback(() => {
    navigate(Route.TournamentBracket.replace(':id', tournament?.id ?? ''));
  }, [tournament?.id, navigate]);

  const tournamentSettings = useMemo((): TournamentSettingsOption[] => {
    return [
      {
        label: 'prizes',
        value: <GameCurrency amount={currentTotalPrize} />,
      },
      {
        label: isSinglePlayer ? 'players' : 'teams',
        value: teamsSize,
      },
      {
        label: 'team size',
        value: <TeamSizeFormat size={tournament?.teamsSettings?.teamSize} />,
      },
      {
        label: 'entry fee',
        value: (
          <GameCurrencyWithFee amount={tournament?.prizeAndDistribution?.playerEntryFee || 0} />
        ),
      },
      {
        label: 'format',
        value: (
          <TournamentTypeFormat
            format={tournament?.matchSetupSettings?.eliminationFormat as TournamentType}
          />
        ),
      },
      {
        label: (
          <>
            <>Best Of</>
            <Box pl={1} display="inline">
              <Info>
                Custom best-of could be applied per round. See{' '}
                <Link onClick={openBracketHandler}>Brackets</Link>
              </Info>
            </Box>
          </>
        ),
        value: <ValueOrDefaultFormat value={tournament?.matchSetupSettings?.bestOf} />,
      },
    ];
  }, [openBracketHandler, currentTotalPrize, tournament, teamsSize, isSinglePlayer]);

  const matchSetupSettings = useTournamentMatchSetupSettings(tournament);

  return (
    <Box p={3} bgcolor="background.paper">
      <TournamentSettings tournamentSettings={tournamentSettings} title="Tournament Details" />
      <Box mt={3}>
        <TournamentSettings tournamentSettings={matchSetupSettings} title="Game Settings" />
      </Box>
      <Box mt={3}>
        <TournamentPrizes distributionBreakdown={currentDistributionBreakdown} />
      </Box>
      {tournament.description && (
        <Box mt={3}>
          <TournamentDescription content={tournament.description} />
        </Box>
      )}
      <Box mt={3}>
        <TournamentRules tournament={tournament} />
      </Box>
      <Box mt={3}>
        <TournamentAdmins admins={tournament?.admins} owner={tournament?.owner as UserEntity} />
      </Box>
      <Box mt={3}>
        <TournamentSupport />
      </Box>
    </Box>
  );
};
